import { SecondaryItem, ESecondaryItemType } from "@similarweb/ui-components";

import { isLocked, isHidden } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { getUnlockBadge } from "app/components/SideBar/getUnlockBadge";
import { reduceSidebarLockedFeaturesTest } from "app/components/sidebar3/secondary-side-bar/utils";
import { translate } from "app/filters/i18nFilter";
import { ECategoryAudienceType } from "app/pages/website-analysis/website-structure/pages/AudienceWrapper";
import { applyNavItemPermissions } from "app/pages/workspace/common/workspacesUtils";
import { allTrackers } from "app/services/track/track";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { isNoTouch, isTrialOrAfterTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";

import type { INavItem } from "../../../types";

const ignoreSearchKeywordsParams = {
    IncludeOrganic: null,
    IncludePaid: null,
    IncludeBranded: null,
    IncludeNoneBranded: null,
    IncludeNewKeywords: null,
    IncludeTrendingKeywords: null,
    IncludeQuestions: null,
    limits: null,
    BooleanSearchTerms: null,
    ExcludeUrls: null,
    IncludeUrls: null,
    IncludeTerms: null,
    ExcludeTerms: null,
};
const ignoreDisplayGroupParams = {
    sort: null,
    domain: null,
};

const isGroupHidden = (subItems: INavItem[]): boolean => {
    return subItems.length > 0 && subItems.every((subItem) => subItem.hidden === true);
};

const isWebsiteContentTouchHookDummyPageExperimentOn = (): boolean =>
    GrowthbookService.growthbookInstance.isOn(TestsNames.TOUCH_HOOKS_DUMMY_PAGE) &&
    (isNoTouch(swSettings) || isTrialOrAfterTrialUser(swSettings));

/** Workspace `applyNavItemPermissions` overwrites sub-item locks; restore dummy-page nav for Website Content only. */
const patchWebsiteContentNavForDummyPageExperiment = (navList: INavItem[]): INavItem[] => {
    if (!isWebsiteContentTouchHookDummyPageExperimentOn()) {
        return navList;
    }

    return navList.map((item) => {
        if (item.name !== "overview" || !item.subItems) {
            return item;
        }

        return {
            ...item,
            subItems: item.subItems.map((subItem) =>
                subItem.name === "websiteContent" ? { ...subItem, lockIcon: false, hidden: false } : subItem,
            ),
        };
    });
};

const handleTracker = (subMenuName, isOpen, hasActiveChild) => {
    if (hasActiveChild) {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `sidebar/expand/${subMenuName || ""}`);
    } else {
        allTrackers.dispatchTrackEvent(
            "Internal Link",
            "click",
            `sidebar/${isOpen ? "collapse" : "expand"}/${subMenuName || ""}`,
        );
    }
};

const websiteAnalysisGroup = (): { navList: INavItem[] } => {
    const isDummyPageTestOn = isWebsiteContentTouchHookDummyPageExperimentOn();
    const trafficSubItems: INavItem[] = [
        {
            title: "analysis.traffic.engagement.title",
            name: "traffic-traffic&engagement",
            state: "digitalsuite_website_trafficandengagement",
            options: {
                isUSStatesSupported: true,
                isVirtualSupported: false,
            },
            lockIcon: isLocked(swSettings.components.WsWebTrafficAndEngagement),
            hidden: isHidden(swSettings.components.WsWebTrafficAndEngagement),
            upsellBadgeComponent: isLocked(swSettings.components.WsWebTrafficAndEngagement)
                ? getUnlockBadge()
                : undefined,
        },
        {
            title: "analysis.traffic.marketing.channels.title",
            name: "overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "digitalsuite_website_marketingchannels",
            lockIcon: isLocked(swSettings.components.WsWebTrafficChannels),
            hidden: isHidden(swSettings.components.WsWebTrafficChannels),
            upsellBadgeComponent: isLocked(swSettings.components.WsWebTrafficChannels) ? getUnlockBadge() : undefined,
        },
    ];

    return {
        navList: [
            {
                title: "analysis.overview.title",
                name: "overview",
                subItems: [
                    {
                        title: "analysis.overview.performance.title",
                        name: "worldwideOverview",
                        state: "digitalsuite_website_websiteperformance",
                        options: {
                            isUSStatesSupported: true,
                            isVirtualSupported: false,
                        },
                        lockIcon: isLocked(swSettings.components.WsWebOverview),
                        hidden: isHidden(swSettings.components.WsWebOverview),
                    },
                    {
                        title: "analysis.overview.website.content.title",
                        name: "websiteContent",
                        state: "digitalsuite_website_content",
                        options: { isUSStatesSupported: true, isVirtualSupported: false },
                        lockIcon: isDummyPageTestOn ? false : isLocked(swSettings.components.WsWebWebsiteContent),
                        hidden: isDummyPageTestOn ? false : isHidden(swSettings.components.WsWebWebsiteContent),
                        subItems: [
                            {
                                state: "digitalsuite_website_folders",
                                name: "folders",
                                title: translate("analysis.content.folders.title"),
                                hidden: true,
                                options: {
                                    isVirtualSupported: false,
                                    isUSStatesSupported: true,
                                },
                            },
                            {
                                state: "digitalsuite_website_popular_v2",
                                name: "popularPages",
                                title: translate("analysis.content.popular.title"),
                                hidden: true,
                                options: {
                                    isVirtualSupported: false,
                                    isUSStatesSupported: true,
                                },
                            },
                            {
                                state: "digitalsuite_website_lob",
                                name: "lob",
                                title: translate("analysis.content.lob.title"),
                                hidden: true,
                                options: {
                                    isVirtualSupported: false,
                                    isUSStatesSupported: true,
                                },
                            },
                            {
                                state: "digitalsuite_website_subdomains",
                                name: "subdomains",
                                title: translate("analysis.content.subdomains.title"),
                                hidden: true,
                                options: {
                                    isVirtualSupported: false,
                                    isUSStatesSupported: true,
                                },
                            },
                        ],
                    },
                    {
                        title: "digitalsuite.analysis.overview.competitors.similarsites.title",
                        name: "competitiveLandscape",
                        state: "digitalsuite_website_competitivelandscape",
                        options: {
                            isVirtualSupported: false,
                            isUSStatesSupported: true,
                        },
                        lockIcon: isLocked(swSettings.components.WsWebCompetitors),
                        hidden: isHidden(swSettings.components.WsWebCompetitors),
                    },
                ],
            },
            {
                title: "analysis.traffic.title2",
                name: "traffic",
                hidden: isGroupHidden(trafficSubItems),
                subItems: trafficSubItems,
            },
        ],
    };
};

const getAudienceGroup = () => ({
    title: "analysis.audience.title",
    name: "audience",
    state: "digitalsuite_website_audience",
    options: {
        isVirtualSupported: true,
        isUSStatesSupported: true,
    },
    lockIcon: isLocked(swSettings.components.WebAudience),
    hidden:
        isHidden(swSettings.components.WebGeography) &&
        isHidden(swSettings.components.WebDemographics) &&
        isHidden(swSettings.components.WebAudienceInterests) &&
        isHidden(swSettings.components.WebAudienceOverlap),
    subItems: [
        {
            state: "digitalsuite_website_geography",
            name: "geography",
            title: "analysis.audience.geo.title",
            hidden: isHidden(swSettings.components.WebGeography),
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
        },
        {
            state: "digitalsuite_website_demographics",
            name: "demographics",
            title: "analysis.audience.demo.title",
            hidden: isHidden(swSettings.components.WebDemographics),
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
        },
        {
            state: "digitalsuite_website_audienceInterests",
            name: ECategoryAudienceType.AudienceInterests,
            title: "analysis.audience.interests.title",
            hidden: isHidden(swSettings.components.WebAudienceInterests),
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
        },
        {
            state: "digitalsuite_website_audienceOverlap",
            name: "audienceOverlap",
            title: "analysis.audience.overlap.title",
            hidden: isHidden(swSettings.components.WebAudienceOverlap),
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
        },
    ],
    itemComponent: SecondaryItem,
    itemType: ESecondaryItemType.Standalone,
    upsellBadgeComponent: getUnlockBadge(),
});

const getOrganicSearchGroup = () => ({
    title: "competitiveanalysis.organic_search.title",
    name: "searchOverview",
    state: "digitalsuite_website_search_overview",
    options: {
        isVirtualSupported: true,
        isUSStatesSupported: true,
    },
    defaultQueryParams: {
        ...ignoreSearchKeywordsParams,
    },
    lockIcon: isLocked(swSettings.components.WsWebSearchOverview),
    hidden: isHidden(swSettings.components.WsWebSearchOverview),
    itemComponent: SecondaryItem,
    itemType: ESecondaryItemType.Standalone,
    itemComponentProps: {
        upsellIconName:
            isLocked(swSettings.components.WsWebSearchOverview) && getUnlockBadge() === null ? undefined : null,
        badgeComponent: isLocked(swSettings.components.WsWebSearchOverview) ? getUnlockBadge() : undefined,
    },
});

const getReferalGroup = () => ({
    title: "analysis.referrals.title",
    name: "Referral",
    state: "digitalsuite_website_referrals",
    options: {
        isVirtualSupported: true,
        isUSStatesSupported: true,
    },
    lockIcon: isLocked(swSettings.components.WebReferralTraffic),
    hidden:
        isHidden(swSettings.components.WebReferralIncomingTraffic) &&
        isHidden(swSettings.components.WebReferralOutgoingTraffic),
    subItems: [
        {
            title: "analysis.referrals.incoming.title",
            name: "referrals",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "digitalsuite_website_referrals_incomingtraffic",
            lockIcon: isLocked(swSettings.components.WebReferralIncomingTraffic),
            hidden: isHidden(swSettings.components.WebReferralIncomingTraffic),
            defaultQueryParams: {
                limits: null,
                ExcludeUrls: null,
                IncludeUrls: null,
            },
            upsellBadgeComponent: isLocked(swSettings.components.WebReferralIncomingTraffic)
                ? getUnlockBadge()
                : undefined,
        },
        {
            title: "analysis.referrals.outgoing.title",
            name: "outgoing",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: true,
            },
            state: "digitalsuite_website_referrals_outgoingtraffic",
            lockIcon: isLocked(swSettings.components.WebReferralOutgoingTraffic),
            hidden: isHidden(swSettings.components.WebReferralOutgoingTraffic),
        },
    ],
    itemComponent: SecondaryItem,
    itemType: ESecondaryItemType.Standalone,
    upsellBadgeComponent: isLocked(swSettings.components.WebReferralTraffic) ? getUnlockBadge() : undefined,
});

const getSocialGroup = () => ({
    title: "analysis.social.title2",
    name: "social",
    options: {
        isVirtualSupported: true,
        isUSStatesSupported: true,
    },
    state: "digitalsuite_website_social_overview",
    lockIcon: isLocked(swSettings.components.WsWebSocialTrafficOverview),
    hidden: isHidden(swSettings.components.WsWebSocialTrafficOverview),
    itemComponent: SecondaryItem,
    itemType: ESecondaryItemType.Standalone,
    itemComponentProps: {
        upsellIconName:
            isLocked(swSettings.components.WsWebSocialTrafficOverview) && getUnlockBadge() === null ? undefined : null,
        badgeComponent: isLocked(swSettings.components.WsWebSocialTrafficOverview) ? getUnlockBadge() : undefined,
    },
});

const getMonetizationGroup = () => ({
    title: "analysis.monetization.title2",
    name: "monetization",
    hidden:
        isHidden(swSettings.components.WsWebMonetizationAdvertisers) &&
        isHidden(swSettings.components.WsWebMonetizationNetworks),
    subItems: [
        {
            title: "analysis.monetization.advertisers.title",
            name: "paid-outgoing",
            state: "digitalsuite_websites_paidoutgoing_advertisers",
            lockIcon: isLocked(swSettings.components.WsWebMonetizationAdvertisers),
            hidden: isHidden(swSettings.components.WsWebMonetizationAdvertisers),
            upsellBadgeComponent: isLocked(swSettings.components.WsWebMonetizationAdvertisers)
                ? getUnlockBadge()
                : undefined,
        },
        {
            title: "digitalsuite.analysis.monetization.title",
            name: "paid-outgoing",
            state: "digitalsuite_websites_paidoutgoing_monetization_networks",
            lockIcon: isLocked(swSettings.components.WsWebMonetizationNetworks),
            hidden: isHidden(swSettings.components.WsWebMonetizationNetworks),
            upsellBadgeComponent: isLocked(swSettings.components.WsWebMonetizationNetworks)
                ? getUnlockBadge()
                : undefined,
        },
    ],
});

const createDisplayGroup = (): INavItem => {
    const displaySubItems: INavItem[] = [
        {
            title: "display.overview.title",
            name: "displayOverview",
            id: "competitiveanalysis_display_overview",
            state: "digitalsuite_website_display_overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: false,
            },
            lockIcon: isLocked(swSettings.components.WsWebDisplayTrafficOverview),
            hidden: isHidden(swSettings.components.WsWebDisplayTrafficOverview),
            defaultQueryParams: {
                ...ignoreDisplayGroupParams,
            },
            upsellBadgeComponent: isLocked(swSettings.components.WsWebDisplayTrafficOverview)
                ? getUnlockBadge()
                : undefined,
        },
        {
            title: "analysis.publisher.overview.title",
            name: entityTypeMap.publisher,
            state: "digitalsuite_website_publisher_overview",
            options: {
                isVirtualSupported: true,
                isUSStatesSupported: false,
            },
            lockIcon: isLocked(swSettings.components.WsWebDisplayTrafficOverview),
            hidden: isHidden(swSettings.components.WsWebDisplayTrafficOverview),
            defaultQueryParams: {
                ...ignoreDisplayGroupParams,
            },
            upsellBadgeComponent: isLocked(swSettings.components.WsWebDisplayTrafficOverview)
                ? getUnlockBadge()
                : undefined,
        },
    ];

    return {
        title: "analysis.display.title2",
        name: "display",
        isNew: true,
        hidden: isGroupHidden(displaySubItems),
        subItems: displaySubItems,
    };
};

const getWebsiteStructureGroup = () => [
    {
        title: "analysis.overview.technographics.title",
        name: "technographics",
        state: "digitalsuite_websites_technologiesOverview",
        options: {
            isVirtualSupported: false,
            isUSStatesSupported: false,
        },
        lockIcon: isLocked(swSettings.components.WebTechnographics),
        hidden: isHidden(swSettings.components.WebTechnographics),
        itemComponent: SecondaryItem,
        itemType: ESecondaryItemType.Standalone,
    },

    {
        title: "Sneak Peek",
        name: "websites",
        hidden: !swSettings.components.Home.resources.HasNewSneakPeek,
        subItems: [
            {
                title: "Data Query",
                name: "sneakpeekQuery",
                options: {
                    isVirtualSupported: false,
                    isUSStatesSupported: true,
                },
                state: "digitalsuite_website_sneakpeekQuery",
            },
            {
                title: "Data Results",
                name: "sneakpeekResults",
                options: {
                    isVirtualSupported: false,
                    isUSStatesSupported: true,
                },
                state: "digitalsuite_website_sneakpeekResults",
            },
        ],
    },
];

export const websiteAnalysisNavObj = (): { navList: INavItem[] } => {
    return {
        navList: patchWebsiteContentNavForDummyPageExperiment(
            [
                ...websiteAnalysisGroup().navList,
                getAudienceGroup(),
                getOrganicSearchGroup(),
                getReferalGroup(),
                createDisplayGroup(),
                getSocialGroup(),
                getMonetizationGroup(),
                ...getWebsiteStructureGroup(),
            ]
                .map(applyNavItemPermissions)
                .reduce(reduceSidebarLockedFeaturesTest, []),
        ),
    };
};
